import { useEffect, useRef } from 'react';
import { useFormControlContext } from './useFormControl';
import Slider, { SliderProps } from 'rc-slider';
import cx from 'classnames';

export interface IFormControlSliderGroupProps extends SliderProps {}

export const FormControlSlider = (props: IFormControlSliderGroupProps) => {
  const { disabled } = props;
  const wrapperRef = useRef<HTMLInputElement | null>(null);
  const { readOnly } = useFormControlContext() || {};

  const marksToString = Object.entries(props.marks || {}).reduce<string>((acc, [value, label]) => {
    return acc + `${value}:${label},`;
  }, '');

  useEffect(() => {
    const recalculateMarkDimensions = () => {
      if (wrapperRef.current) {
        const marksElements = wrapperRef.current.querySelectorAll('.rc-slider-mark-text') as NodeListOf<HTMLElement>;
        const sliderWidth = wrapperRef.current.clientWidth;

        // Set max-width for each mark text element
        marksElements.forEach((markElement, index) => {
          const leftPercentage = parseFloat(markElement.style.left);
          const leftPixelValue = (leftPercentage / 100) * sliderWidth;

          let maxWidth;
          if (leftPercentage === 0) {
            // First mark - take 60% of available space to the next mark
            const nextMarkElement = marksElements[index + 1];
            const nextLeftPercentage = nextMarkElement ? parseFloat(nextMarkElement.style.left) : 100;
            const nextLeftPixelValue = (nextLeftPercentage / 100) * sliderWidth;
            maxWidth = (nextLeftPixelValue - leftPixelValue) * 0.6;
            markElement.style.transform = 'translateX(-10%)';
          } else if (leftPercentage === 100) {
            // Last mark - take 60% of available space to the previous mark
            const prevMarkElement = marksElements[index - 1];
            const prevLeftPercentage = prevMarkElement ? parseFloat(prevMarkElement.style.left) : 0;
            const prevLeftPixelValue = (prevLeftPercentage / 100) * sliderWidth;
            maxWidth = (leftPixelValue - prevLeftPixelValue) * 0.6;
            markElement.style.transform = 'translateX(-90%)';
          } else {
            // Intermediate marks - take 80% of the available space between adjacent marks
            const prevMarkElement = marksElements[index - 1];
            const nextMarkElement = marksElements[index + 1];
            const prevLeftPercentage = prevMarkElement ? parseFloat(prevMarkElement.style.left) : 0;
            const nextLeftPercentage = nextMarkElement ? parseFloat(nextMarkElement.style.left) : 100;
            const prevLeftPixelValue = (prevLeftPercentage / 100) * sliderWidth;
            const nextLeftPixelValue = (nextLeftPercentage / 100) * sliderWidth;
            const prevSpace = leftPixelValue - prevLeftPixelValue;
            const nextSpace = nextLeftPixelValue - leftPixelValue;
            maxWidth = Math.min(prevSpace, nextSpace) * 0.8;
          }

          markElement.style.maxWidth = `${maxWidth}px`;
        });

        // Adjust the height of the mark container
        const maxHeight = Math.max(...Array.from(marksElements).map((el) => el.clientHeight));
        const markContainerElement = wrapperRef.current.querySelector('.rc-slider-mark') as HTMLElement;
        if (markContainerElement) {
          markContainerElement.style.height = `${maxHeight}px`;
          markContainerElement.style.visibility = 'visible';
        }
      }
    };

    recalculateMarkDimensions();

    const handleResize = () => {
      recalculateMarkDimensions();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [marksToString]);

  return (
    <div ref={wrapperRef}>
      <Slider
        className={cx('rio-rc-slider', marksToString && 'rio-rc-slider--marks')}
        disabled={disabled || readOnly}
        {...props}
      />
    </div>
  );
};
